import React, { useContext, useEffect, useMemo, useState } from "react"
import Logo from "../img/max222.png"
import { SlBag, SlEarphonesAlt, SlMenu } from "react-icons/sl"
import {
    IoClose,
    IoChevronForwardOutline,
    IoCameraOutline,
    IoVideocamOutline,
    IoFlashOutline,
    IoBulbOutline,
    IoRefreshOutline,
    IoApertureOutline,
} from "react-icons/io5"
import { Link } from "react-router-dom"
import SearchForm from "./SearchForm"
import Cart from "./Cart"
import { CartContext } from "../context/CartContext"

// ✅ API icon string -> component
const ICON_MAP = {
    IoCameraOutline,
    IoApertureOutline,
    IoVideocamOutline,
    IoFlashOutline,
    IoBulbOutline,
    IoRefreshOutline,
}
const DEFAULT_ICON = IoCameraOutline

// ✅ BU YERNI O'ZINGIZGA MOSLANG
const API_URL = "https://api.max-photo.uz/api/categories/" // masalan: /api/categories/

const Header = () => {
    const { isOpen, setIsOpen, itemsAmount } = useContext(CartContext)

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [contactsOpen, setContactsOpen] = useState(false)
    const [megaMenuOpen, setMegaMenuOpen] = useState(false)

    const [megaMenuData, setMegaMenuData] = useState([])
    const [loadingMenu, setLoadingMenu] = useState(true)
    const [activeCategoryId, setActiveCategoryId] = useState(null)

    // ✅ Load categories from API
    useEffect(() => {
        const controller = new AbortController()

        async function loadMenu() {
            try {
                setLoadingMenu(true)
                const res = await fetch(API_URL, { signal: controller.signal })
                if (!res.ok) throw new Error(`HTTP ${res.status}`)

                const data = await res.json()

                // normalize + icon mapping
                const normalized = (Array.isArray(data) ? data : []).map((cat) => ({
                    ...cat,
                    icon: ICON_MAP?.[cat?.icon] || DEFAULT_ICON,
                    subcategories: Array.isArray(cat?.subcategories) ? cat.subcategories : [],
                }))

                setMegaMenuData(normalized)

                // set default active category
                if (normalized.length > 0) {
                    setActiveCategoryId((prev) => prev ?? normalized[0].id)
                } else {
                    setActiveCategoryId(null)
                }
            } catch (e) {
                if (e?.name !== "AbortError") {
                    console.error("Failed to load categories:", e)
                    setMegaMenuData([])
                    setActiveCategoryId(null)
                }
            } finally {
                setLoadingMenu(false)
            }
        }

        loadMenu()
        return () => controller.abort()
    }, [])

    const activeCategory = useMemo(() => {
        return megaMenuData.find((c) => c.id === activeCategoryId) || null
    }, [megaMenuData, activeCategoryId])

    const subcategories = Array.isArray(activeCategory?.subcategories)
        ? activeCategory.subcategories
        : []

    return (
        <>
            <header className="bg-primary py-4 lg:py-6 sticky top-0 z-30 shadow-xl">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between gap-4">
                        {/* Мобильные кнопки */}
                        <div className="flex items-center gap-4 xl:hidden">
                            <button
                                onClick={() => setMobileMenuOpen(true)}
                                className="text-2xl text-white hover:scale-110 transition"
                            >
                                <SlMenu />
                            </button>
                        </div>

                        <Link to="/" className="flex-shrink-0">
                            <img src={Logo} alt="Logo" className="w-32 sm:w-40 lg:w-52 h-auto" />
                        </Link>

                        {/* Десктоп: Каталог + Поиск */}
                        <div className="hidden xl:flex items-center flex-1 justify-center gap-8">
                            <button
                                onClick={() => setMegaMenuOpen(!megaMenuOpen)}
                                className="flex items-center gap-3 px-8 py-3 bg-white/10 hover:bg-white/20 rounded-2xl text-white font-bold text-lg transition-all duration-300 backdrop-blur-sm border border-white/10 hover:border-accent/40"
                            >
                                {megaMenuOpen ? <IoClose className="text-2xl" /> : <SlMenu className="text-2xl" />}
                                <span>Каталог</span>
                            </button>

                            <div className="flex-1 max-w-2xl">
                                <SearchForm />
                            </div>
                        </div>

                        <div className="flex items-center gap-x-1 xs:gap-x-1.5 sm:gap-x-2 lg:gap-x-3 flex-shrink-0">
                            {/* Cart Icon */}
                            <button
                                onClick={() => setIsOpen(!isOpen)}
                                className="relative cursor-pointer group"
                                aria-label="Корзина"
                            >
                                <div className="p-1.5 xs:p-2 sm:p-2.5 hover:bg-white/10 rounded-lg transition-all duration-200 border border-transparent hover:border-accent/30">
                                    <SlBag className="text-lg xs:text-xl sm:text-2xl group-hover:scale-110 transition-transform duration-200 text-white" />
                                    {itemsAmount > 0 && (
                                        <div className="bg-accent text-white absolute w-[16px] h-[16px] xs:w-[18px] xs:h-[18px] sm:w-[20px] sm:h-[20px] rounded-full -top-0.5 -right-0.5 text-[9px] xs:text-[10px] sm:text-[12px] flex justify-center items-center font-bold shadow-lg animate-pulse">
                                            {itemsAmount}
                                        </div>
                                    )}
                                </div>
                            </button>

                            {/* Cart Sidebar */}
                            <div
                                className={`${isOpen ? "right-0" : "-right-full"
                                    } bg-[#0e0f10] shadow-xl fixed top-0 bottom-0 w-full z-10 md:max-w-[500px] transition-all duration-300`}
                            >
                                <Cart />
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* ✅ DESKTOP MEGA MENU */}
            {megaMenuOpen && (
                <>
                    <div className="fixed inset-0 bg-black/70 z-40" onClick={() => setMegaMenuOpen(false)} />

                    <div className="fixed top-24 left-1/2 -translate-x-1/2 w-full max-w-7xl z-50">
                        <div className="bg-primary/98 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden animate-in fade-in slide-in-from-top-4 duration-300">
                            <div className="flex h-[480px]">
                                {/* Левая панель */}
                                <div className="w-96 bg-white/5 border-r border-white/10 overflow-y-auto custom-scrollbar">
                                    {loadingMenu ? (
                                        <div className="p-6 text-white/70">Loading...</div>
                                    ) : megaMenuData.length === 0 ? (
                                        <div className="p-6 text-white/70">Категории не найдены</div>
                                    ) : (
                                        megaMenuData.map((cat) => {
                                            const Icon = cat.icon || DEFAULT_ICON
                                            const isActive = activeCategoryId === cat.id

                                            return (
                                                <button
                                                    key={cat.id}
                                                    onMouseEnter={() => setActiveCategoryId(cat.id)}
                                                    onClick={() => setActiveCategoryId(cat.id)}
                                                    className={`w-full flex items-center justify-between px-6 py-5 transition-all duration-200 group ${isActive
                                                        ? "bg-accent/20 text-accent border-r-4 border-accent"
                                                        : "hover:bg-white/10 text-white/90"
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <Icon className="text-2xl text-accent" />
                                                        <span className="font-medium text-left">{cat.title}</span>
                                                    </div>
                                                    <IoChevronForwardOutline className="text-white/50 group-hover:text-accent transition" />
                                                </button>
                                            )
                                        })
                                    )}
                                </div>

                                {/* Правая панель */}
                                <div className="flex-1 p-10 bg-gradient-to-br from-primary/95 to-primary/100 overflow-hidden flex flex-col">
                                    {activeCategory ? (
                                        <Link
                                            to={`/products/${activeCategory.id}`}
                                            onClick={() => setMegaMenuOpen(false)}
                                            className="inline-block text-3xl font-bold text-white mb-10 tracking-tight hover:text-accent transition-colors"
                                        >
                                            {activeCategory.title}
                                        </Link>
                                    ) : (
                                        <h3 className="text-3xl font-bold text-white mb-10 tracking-tight">
                                            Каталог
                                        </h3>
                                    )}

                                    <div className="flex-1 overflow-y-auto custom-scrollbar pr-4 pb-8">
                                        <div className="grid grid-cols-3 gap-12">
                                            {subcategories.map((section, i) => {
                                                const items = Array.isArray(section?.items) ? section.items : []
                                                return (
                                                    <div key={i} className="space-y-5 min-w-0">
                                                        <h4 className="text-accent font-bold uppercase tracking-wider text-sm">
                                                            {section?.title}
                                                        </h4>
                                                        <ul className="space-y-3">
                                                            {items.map((item, j) => (
                                                                <li key={j}>
                                                                    <Link
                                                                        to={`/search?query=${item}`}
                                                                        onClick={() => setMegaMenuOpen(false)}
                                                                        className="block text-white/80 hover:text-accent transition-all duration-200 hover:translate-x-1 text-sm font-light"
                                                                    >
                                                                        {item}
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                                {/* end right */}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ✅ MOBILE MENU */}
            {mobileMenuOpen && (
                <>
                    <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setMobileMenuOpen(false)} />
                    <div className="fixed left-0 top-0 bottom-0 w-80 bg-primary z-50 shadow-2xl overflow-y-auto">
                        <div className="p-4">
                            {/* Close */}
                            <button
                                onClick={() => setMobileMenuOpen(false)}
                                className="absolute top-4 right-4 text-white text-2xl hover:text-accent transition"
                            >
                                <IoClose />
                            </button>

                            {/* Logo */}
                            <div className="mb-6 pt-2">
                                <img src={Logo} alt="Logo" className="w-40 h-auto" />
                            </div>

                            {/* Search */}
                            <div className="mb-6">
                                <SearchForm setMobileMenuOpen={setMobileMenuOpen} />
                            </div>

                            {/* Catalog Items */}
                            <div className="space-y-2">
                                {loadingMenu ? (
                                    <div className="px-4 py-3 text-white/70">Loading...</div>
                                ) : megaMenuData.length === 0 ? (
                                    <div className="px-4 py-3 text-white/70">Категории не найдены</div>
                                ) : (
                                    megaMenuData.map((cat) => {
                                        const Icon = cat.icon || DEFAULT_ICON
                                        const catSubs = Array.isArray(cat?.subcategories) ? cat.subcategories : []

                                        return (
                                            <details key={cat.id} className="group">
                                                <summary className="flex items-center justify-between px-4 py-3 bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition text-white">
                                                    <div className="flex items-center gap-3">
                                                        <Icon className="text-xl text-accent" />
                                                        <span className="font-medium">{cat.title}</span>
                                                    </div>
                                                    <IoChevronForwardOutline className="text-white/50 group-open:rotate-90 transition" />
                                                </summary>

                                                <div className="mt-2 ml-4 space-y-2">
                                                    <Link
                                                        to={`/products/${cat.id}`}
                                                        onClick={() => setMobileMenuOpen(false)}
                                                        className="block px-4 py-2 text-accent text-sm font-bold hover:bg-white/5 rounded transition border-b border-white/10"
                                                    >
                                                        Посмотреть все в {cat.title}
                                                    </Link>

                                                    {catSubs.map((section, i) => {
                                                        const items = Array.isArray(section?.items) ? section.items : []
                                                        return (
                                                            <details key={i} className="group/sub">
                                                                <summary className="px-4 py-2 text-accent text-sm font-semibold cursor-pointer hover:bg-white/5 rounded transition">
                                                                    {section?.title}
                                                                </summary>
                                                                <ul className="mt-1 ml-4 space-y-1">
                                                                    {items.map((item, j) => (
                                                                        <li key={j}>
                                                                            <Link
                                                                                to={`/search?query=${item}`}
                                                                                onClick={() => setMobileMenuOpen(false)}
                                                                                className="block px-4 py-2 text-white/70 text-sm hover:text-white hover:bg-white/5 rounded transition"
                                                                            >
                                                                                {item}
                                                                            </Link>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </details>
                                                        )
                                                    })}
                                                </div>
                                            </details>
                                        )
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ✅ Cart overlay */}
            {isOpen && (
                <>
                    <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setIsOpen(false)} />
                    <div className="fixed right-0 top-0 bottom-0 w-full md:max-w-lg bg-[#0e0f10] z-50">
                        <Cart />
                    </div>
                </>
            )}

            {/* ✅ Contacts floating */}
            <div className="fixed bottom-4 right-4 z-50">
                <div className="relative">
                    <div
                        className={`absolute bottom-16 right-0 bg-primary/95 backdrop-blur-xl rounded-2xl shadow-2xl p-5 min-w-64 transition-all duration-300 border border-white/10 ${contactsOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
                            }`}
                    >
                        <div className="flex flex-col gap-4">
                            <a
                                href="tel:909601201"
                                className="flex items-center gap-4 text-white hover:text-accent p-4 hover:bg-white/10 rounded-xl transition"
                            >
                                <SlEarphonesAlt className="text-2xl" />
                                <span className="font-medium">(90) 960-12-01</span>
                            </a>
                            <a
                                href="tel:+998909083226"
                                className="flex items-center gap-4 text-white hover:text-accent p-4 hover:bg-white/10 rounded-xl transition"
                            >
                                <SlEarphonesAlt className="text-2xl" />
                                <span className="font-medium">(90) 960-12-03</span>
                            </a>
                        </div>
                    </div>

                    <button
                        onClick={() => setContactsOpen(!contactsOpen)}
                        className="bg-accent hover:bg-accent/90 text-white rounded-full p-5 shadow-2xl transition-all hover:scale-110 active:scale-95"
                    >
                        {contactsOpen ? <IoClose className="text-3xl" /> : <SlEarphonesAlt className="text-3xl" />}
                    </button>
                </div>
            </div>


        </>
    )
}

export default Header
